import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import type {RegistrationRequest} from '@yandex-delivery/api/service/registration/types'
import {toUndefined} from '@yandex-delivery/utils'
import {useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {usePaymentCardService, useRegistrationService} from '../../../../api/payment'
import {useConfigContext} from '../../../../contexts/ConfigContext'
import {FieldName} from '../../../../lib/fieldNames'
import type {ChangePaymentCardModalProps} from './types'

export function useChangePaymentCardModal({getBody}: ChangePaymentCardModalProps) {
  const {fetchCards, fetchCardBoundContext, fetchPaymentMethods, fetchNotificationsAgreements} = usePaymentCardService()
  const {fetchCardBound, fetchRegistration} = useRegistrationService()
  const {countryCode} = useConfigContext()
  const {setValue} = useFormContext()

  // useState вместо useMemo, потому то в документации написано, что useMemo может вызываться повторно
  // тогда как useState гарантирует единственный вызов
  const [state] = useState(() => {
    // возможно getBody стоит положить в useRef, потому что useState сохранит ссылку в замыкании
    const body: RegistrationRequest = Object.assign({country: countryCode}, getBody())
    // начинаем скрытую регистрацию пользователя, пока он вводит (или выбирает) данные карты
    // игнорируем ошибки, если что-то пойдет не так, то попробуем еще раз перед привязкой карты
    const registrationPromise = fetchRegistration(body).catch(toUndefined)

    return {
      fetchCardBoundContext,
      fetchCards,
      fetchNotificationsAgreements,
      fetchBoundCard(nextCard: PaymentCard, isNotificationsAllowed: boolean): Promise<PaymentCard> {
        return registrationPromise
          .then(response => {
            // если на старте зарегистрироваться не получилось, то пробуем регистрировать еще раз
            if (response === undefined) {
              return fetchRegistration(body)
            }

            return response
          })
          .then(fetchCardBound(nextCard, countryCode, isNotificationsAllowed))
          .then(res => {
            void fetchPaymentMethods({mode: 'employee'}, countryCode).then(result => {
              const selected = result.methods.find(method => method.is_selected)
              if (selected) {
                setValue(`${FieldName.PaymentInfo}.method_id`, selected.id)
                setValue(`${FieldName.PaymentInfo}.type`, selected.display?.type)
                setValue(`${FieldName.PaymentInfo}.has_disable_reason`, selected.details.is_disabled)
                setValue(`${FieldName.PaymentInfo}.kind`, result.selected_method?.payment_method_kind)
              }
            })
            return res
          })
      },
    } as const
  })

  return state
}
